import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBrand, logoSrc } from "@/lib/brand";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { Search, X, Plus, ChevronDown, ChevronRight, Bell, Wrench, Target, Headphones, CheckSquare, Send, FileText, MoreHorizontal, User, Settings, Star, HelpCircle, Compass, LogOut, MapPin, Store, Users, Newspaper, ClipboardList, Calendar, Home, Loader2 } from "lucide-react";

import { OrbitAI } from "@/components/OrbitAI";

const BLUE = "var(--brand, #1d4ed8)";

/* ── Logo ─────────────────────────────────────────────────────────────── */
export function OrbitLogo() {
  const navigate = useNavigate();
  const { tenant } = useBrand();
  const src = logoSrc(tenant);
  if (tenant && src) return (
    <button onClick={() => navigate("/")} data-testid="topbar-logo" className="hidden sm:flex items-center px-1 h-12"><img src={src} alt={tenant.name} data-testid="topbar-tenant-logo" className="max-h-10 max-w-[180px] object-contain"/></button>
  );
  if (tenant) return (
    <button onClick={() => navigate("/")} data-testid="topbar-logo" className="hidden sm:flex flex-col items-start leading-none px-1">
      <span className="text-2xl font-black tracking-tight text-[#0b1b3a] truncate max-w-[220px]" data-testid="topbar-tenant-name">{tenant.name}</span>
      <span className="text-[9px] font-bold tracking-[0.18em] text-[#1d4ed8] uppercase">propulsé par Orbit</span>
    </button>
  );
  return (
    <button onClick={() => navigate("/")} data-testid="topbar-logo" className="hidden sm:flex flex-col items-start leading-none px-1">
      <span className="text-2xl font-black tracking-tight text-[#0b1b3a]">Orbit</span>
      <span className="text-[9px] font-bold tracking-[0.18em] text-[#1d4ed8] uppercase">Intranet Groupe</span>
    </button>
  );
}

/* ── Fil d'Ariane ─────────────────────────────────────────────────────── */
const LABELS = { ops: "Centre des opérations", cockpit: "Cockpit", interventions: "Activités", plans: "Plans d'action", alerts: "Alertes terrain", kpi: "KPI Modules", "ca-daily": "CA Jour", achats: "Achats", hygiene: "Hygiène", rh: "RH", support: "Support", maintenance: "Maintenance", marketing: "Marketing", "moyens-generaux": "Moyens Généraux", journal: "Intranet", documents: "Documents", calendar: "Calendrier", notifications: "Notifications", profile: "Profil", admin: "Administration", stores: "Parc", classement: "Classement" };
const SECTIONS = { accueil: "Accueil", wasagram: "WasaGram", actualites: "Actualités", annuaire: "Annuaire", ressources: "Ressources", vie: "Vie d'entreprise" };
export function Breadcrumb() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, i) => ({ label: LABELS[p] || p.replace(/-/g, " "), to: "/" + parts.slice(0, i + 1).join("/") }));
  const sec = new URLSearchParams(search).get("section");
  if (parts[0] === "journal" && sec && SECTIONS[sec]) crumbs.push({ label: SECTIONS[sec], to: pathname + search });
  return (
    <nav className="hidden lg:flex items-center gap-1 text-sm min-w-0" data-testid="topbar-breadcrumb">
      <button onClick={() => navigate("/")} className="p-1.5 rounded-md text-[#1d4ed8] hover:bg-slate-100"><Home className="w-4 h-4"/></button>
      {crumbs.map((c, i) => (
        <span key={c.to} className="flex items-center gap-1 min-w-0">
          <ChevronRight className="w-3.5 h-3.5 text-slate-300"/>
          <button onClick={() => navigate(c.to)} className={`truncate max-w-[160px] px-1 ${i === crumbs.length - 1 ? "font-semibold text-[#0b1b3a]" : "text-slate-500 hover:text-slate-800"}`}>{c.label}</button>
        </span>
      ))}
    </nav>
  );
}

/* ── Popover util ─────────────────────────────────────────────────────── */
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return { open, setOpen, ref };
}

/* ── Recherche universelle ─────────────────────────────────────────────── */
const TYPES = [{ k: "all", l: "Tous" }, { k: "document", l: "Documents" }, { k: "person", l: "Collaborateurs" }, { k: "news", l: "Actualités" }, { k: "store", l: "Magasins" }, { k: "more", l: "Plus" }];
const TYPE_ICON = { document: [FileText, "bg-[#e0e9ff] text-[#1d4ed8]"], person: [User, "bg-[#e0e9ff] text-[#1d4ed8]"], news: [Newspaper, "bg-[#fce7f3] text-[#be185d]"], store: [Store, "bg-[#dcfce7] text-[#15803d]"], mission: [ClipboardList, "bg-[#ffedd5] text-[#c2410c]"], plan: [Target, "bg-[#ede9fe] text-[#6d28d9]"], service: [Headphones, "bg-slate-100 text-slate-700"] };
const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";

export function UniversalSearch() {
  const navigate = useNavigate();
  const { open, setOpen, ref } = usePopover();
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [res, setRes] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [a, b] = await Promise.all([api.get("/intranet-search", { params: { q, limit: 6 } }), api.get("/search", { params: { q } }).catch(() => ({ data: {} }))]);
        const items = [];
        (a.data.documents || []).forEach(d => items.push({ type: "document", title: d.original_filename, sub: "Document", date: d.created_at, to: null, href: d.storage_path }));
        (a.data.persons || []).forEach(p => items.push({ type: "person", title: p.name, sub: `${p.sub_role || p.role}`, user: p, to: "/journal?section=annuaire" }));
        (a.data.news || []).forEach(n => items.push({ type: "news", title: n.title, sub: `Actualité · ${n.category || ""}`, date: n.created_at, to: "/journal?section=actualites" }));
        (a.data.services || []).forEach(s => items.push({ type: "service", title: s.label, sub: "Service", to: s.link }));
        (b.data.stores || []).forEach(s => items.push({ type: "store", title: s.name, sub: `Magasin${s.region ? " · " + s.region : ""}`, to: `/stores/${s.id}` }));
        (b.data.missions || []).forEach(m => items.push({ type: "mission", title: m.title || m.titre, sub: "Mission", date: m.created_at, to: "/ops/interventions" }));
        (b.data.plans || []).forEach(p => items.push({ type: "plan", title: p.title || p.titre, sub: "Plan d'action", date: p.created_at, to: "/ops/plans" }));
        setRes(items);
      } catch { setRes([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const shown = useMemo(() => type === "all" ? res : type === "more" ? res.filter(r => ["mission", "plan", "service"].includes(r.type)) : res.filter(r => r.type === type), [res, type]);
  const go = (r) => { setOpen(false); setQ(""); if (r.href) window.open(`${api.defaults.baseURL}/files/${r.href}`, "_blank"); else if (r.to) navigate(r.to); };

  return (
    <div ref={ref} className="relative flex-1 min-w-0 max-w-2xl" data-testid="topbar-universal-search">
      <button onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }} data-testid="topbar-search-trigger"
              className="w-full min-w-0 flex items-center gap-3 bg-[#f4f6f9] hover:bg-[#eef2ff] rounded-lg px-3 sm:px-4 py-2 text-left transition-colors">
        <Search className="w-5 h-5 text-[#1d4ed8] shrink-0"/>
        <div className="min-w-0 leading-tight"><div className="text-sm text-slate-500 truncate">Rechercher dans Orbit…</div><div className="text-[11px] text-slate-400 truncate hidden md:block">Magasin, collaborateur, document, action, fournisseur…</div></div>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="topbar-search-panel">
          <div className="p-3 border-b border-slate-100 flex items-center gap-2 bg-[#f4f6f9]">
            <Search className="w-4 h-4 text-[#1d4ed8]"/>
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} data-testid="topbar-search" placeholder="Rechercher dans Orbit…" className="flex-1 bg-transparent text-sm outline-none"/>
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400"/> : q && <button onClick={() => setQ("")} data-testid="topbar-search-clear"><X className="w-4 h-4 text-slate-400 hover:text-slate-700"/></button>}
          </div>
          <div className="px-3 py-2 flex gap-1 flex-wrap border-b border-slate-100">
            {TYPES.map(t => <button key={t.k} onClick={() => setType(t.k)} data-testid={`topbar-search-type-${t.k}`} className={`px-3 py-1 rounded-md text-xs font-semibold ${type === t.k ? "bg-[#1d4ed8] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t.l}{t.k === "more" && <ChevronDown className="inline w-3 h-3 ml-0.5"/>}</button>)}
          </div>
          <div className="max-h-[380px] overflow-y-auto" data-testid="topbar-search-results">
            {q.trim().length < 2 ? <div className="px-4 py-6 text-sm text-slate-400 text-center">Tapez au moins 2 caractères…</div> :
             shown.length === 0 && !loading ? <div className="px-4 py-6 text-sm text-slate-400 text-center">Aucun résultat pour « {q} »</div> :
             shown.slice(0, 10).map((r, i) => { const [Icon, tone] = TYPE_ICON[r.type] || TYPE_ICON.service; return (
              <button key={i} onClick={() => go(r)} data-testid={`topbar-search-result-${i}`} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 border-b border-slate-50">
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${tone}`}>{r.user ? <Avatar user={r.user} size={28}/> : <Icon className="w-4 h-4"/>}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#0b1b3a] truncate">{r.title}</div><div className="text-xs text-slate-500 truncate">{r.sub}</div></div>
                {r.date && <span className="text-[11px] text-slate-400 shrink-0">{fmtD(r.date)}</span>}
              </button>); })}
          </div>
          {q.trim().length >= 2 && (
            <button onClick={() => { setOpen(false); navigate(`/journal?section=annuaire`); }} data-testid="topbar-search-all" className="w-full px-4 py-3 text-sm font-semibold text-[#1d4ed8] hover:bg-[#eef2ff] flex items-center justify-center gap-2 border-t border-slate-100"><Search className="w-4 h-4"/>Voir tous les résultats pour « {q} » →</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Menu Créer ───────────────────────────────────────────────────────── */
const CREATE = [
  { icon: Wrench, tone: "bg-[#ede9fe] text-[#6d28d9]", title: "Nouvelle activité", sub: "Visite, audit, formation, formulaire", to: "/ops/interventions?new=1" },
  { icon: Target, tone: "bg-[#fce7f3] text-[#be185d]", title: "Plan d'action", sub: "Suivre et piloter une action", to: "/ops/plans?new=1" },
  { icon: Headphones, tone: "bg-[#e0e9ff] text-[#1d4ed8]", title: "Demande support", sub: "Ouvrir une demande au support", to: "/journal?section=annuaire#contacts" },
  { icon: CheckSquare, tone: "bg-[#dcfce7] text-[#15803d]", title: "Tâche", sub: "Créer une tâche pour votre équipe", to: "/ops/plans?new=1" },
  { icon: Send, tone: "bg-[#ccfbf1] text-[#0f766e]", title: "Publication WasaGram", sub: "Partager une actualité", to: "/journal?tab=fil" },
  { icon: FileText, tone: "bg-[#e0e9ff] text-[#1d4ed8]", title: "Document", sub: "Ajouter un document", to: "/journal?section=ressources" },
];
export function CreateMenu() {
  const navigate = useNavigate();
  const { open, setOpen, ref } = usePopover();
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="topbar-create" className="flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-white text-sm font-semibold shadow-sm hover:bg-blue-800 transition-colors" style={{ background: BLUE }}>
        <Plus className="w-4 h-4"/><span className="hidden sm:inline">Créer</span><ChevronDown className="w-3.5 h-3.5"/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-2xl border border-slate-200 z-50 p-1.5" data-testid="topbar-create-menu">
          {CREATE.map(c => (
            <button key={c.title} onClick={() => { setOpen(false); navigate(c.to); }} data-testid={`topbar-create-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left">
              <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${c.tone}`}><c.icon className="w-4 h-4"/></div>
              <div><div className="text-sm font-semibold text-[#0b1b3a]">{c.title}</div><div className="text-[11px] text-slate-500">{c.sub}</div></div>
            </button>
          ))}
          <button onClick={() => { setOpen(false); navigate("/ops"); }} data-testid="topbar-create-more" className="w-full flex items-center gap-3 px-3 py-2 mt-1 border-t border-slate-100 text-left text-sm text-slate-600 hover:bg-slate-50">
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-slate-100"><MoreHorizontal className="w-4 h-4"/></div>Voir plus d'options<ChevronRight className="w-4 h-4 ml-auto text-slate-400"/>
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Notifications ────────────────────────────────────────────────────── */
const NOTIF_ICON = { intranet_news: [Newspaper, "bg-[#fce7f3] text-[#be185d]"], intranet_proposal: [Newspaper, "bg-[#fce7f3] text-[#be185d]"], mission_assigned: [CheckSquare, "bg-[#dcfce7] text-[#15803d]"], task: [CheckSquare, "bg-[#dcfce7] text-[#15803d]"], support_ticket: [Headphones, "bg-[#e0e9ff] text-[#1d4ed8]"], document: [FileText, "bg-[#e0e9ff] text-[#1d4ed8]"], event: [Calendar, "bg-[#fce7f3] text-[#be185d]"], score_alert: [Target, "bg-[#ffedd5] text-[#c2410c]"] };
const rel = (iso) => { const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000); if (m < 1) return "à l'instant"; if (m < 60) return `il y a ${m} min`; const h = Math.floor(m / 60); if (h < 24) return `il y a ${h} h`; const d = Math.floor(h / 24); return `il y a ${d} jour${d > 1 ? "s" : ""}`; };
export function NotificationsBell({ unread, onRead }) {
  const navigate = useNavigate();
  const { open, setOpen, ref } = usePopover();
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get("/notifications").then(r => setItems((r.data || []).slice(0, 6))).catch(() => {}); }, [open]);
  const click = async (n) => {
    setOpen(false);
    if (!n.read) { try { await api.patch(`/notifications/${n.id}/read`); onRead?.(); } catch {} }
    navigate(n.link || "/notifications");
  };
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="topbar-notifications" className="relative w-10 h-10 grid place-items-center rounded-lg hover:bg-slate-100">
        <Bell className="w-5 h-5 text-slate-600"/>
        {unread > 0 && <span data-testid="topbar-notif-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center text-[10px] font-bold bg-orange-500 text-white rounded-full">{unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-2xl border border-slate-200 z-50" data-testid="topbar-notif-panel">
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <h3 className="font-bold text-[#0b1b3a]">Notifications</h3>
            <button onClick={() => { setOpen(false); navigate("/notifications"); }} data-testid="topbar-notif-all" className="text-xs font-semibold text-[#1d4ed8] hover:underline">Voir tout →</button>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {items.length === 0 && <div className="px-4 py-8 text-sm text-slate-400 text-center">Aucune notification.</div>}
            {items.map(n => { const [Icon, tone] = NOTIF_ICON[n.type] || [Bell, "bg-slate-100 text-slate-600"]; return (
              <button key={n.id} onClick={() => click(n)} data-testid={`topbar-notif-${n.id}`} className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 border-b border-slate-50">
                <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${tone}`}><Icon className="w-5 h-5"/></div>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#0b1b3a] truncate">{n.title}</div>{n.message && <div className="text-xs text-slate-500 line-clamp-2">{n.message}</div>}<div className="text-[11px] text-slate-400 mt-0.5">{rel(n.created_at)}</div></div>
                {!n.read && <span className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: BLUE }}/>}
              </button>); })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Menu utilisateur ─────────────────────────────────────────────────── */
export function UserMenu({ unread, roleLabel }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { open, setOpen, ref } = usePopover();
  const go = (to) => { setOpen(false); navigate(to); };
  const items = [
    { icon: User, l: "Mon profil", to: "/profile", t: "profile" }, { icon: Settings, l: "Mes paramètres", to: "/profile?tab=settings", t: "settings" },
    { icon: Star, l: "Mes favoris", to: "/journal?section=annuaire", t: "favorites" }, { icon: Bell, l: "Mes notifications", to: "/notifications", badge: unread, t: "notifications" },
  ];
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} data-testid="topbar-profile" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-100">
        <Avatar user={user} size={36}/><ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block"/>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-2xl border border-slate-200 z-50" data-testid="topbar-user-menu">
          <div className="p-4 flex items-center gap-3 border-b border-slate-100">
            <Avatar user={user} size={48}/>
            <div className="min-w-0"><div className="font-bold text-[#0b1b3a] truncate">{user?.name}</div><div className="text-xs text-slate-500 truncate">{roleLabel}</div><div className="text-[11px] text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3"/>{user?.location || "Siège – Paris"}</div></div>
          </div>
          <div className="p-1.5">
            {items.map(i => (
              <button key={i.l} onClick={() => go(i.to)} data-testid={`topbar-user-${i.t}`} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700 text-left">
                <i.icon className="w-4 h-4 text-slate-500"/>{i.l}{i.badge > 0 && <span className="ml-auto text-[10px] font-bold bg-orange-500 text-white rounded-full px-2 py-0.5">{i.badge}</span>}
              </button>
            ))}
          </div>
          <div className="p-1.5 border-t border-slate-100">
            <button onClick={() => go("/journal?section=annuaire#contacts")} data-testid="topbar-user-help" className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700 text-left"><HelpCircle className="w-4 h-4 text-slate-500"/>Aide & support</button>
            <button onClick={() => go("/journal?section=actualites")} data-testid="topbar-user-news" className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-sm text-slate-700 text-left"><Compass className="w-4 h-4 text-slate-500"/>Découvrir les nouveautés</button>
          </div>
          <div className="p-1.5 border-t border-slate-100">
            <button onClick={logout} data-testid="topbar-logout" className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-rose-50 text-sm font-semibold text-rose-600 text-left"><LogOut className="w-4 h-4"/>Se déconnecter</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function OrbitIAButton({ onClick }) {
  return (
    <button onClick={onClick} data-testid="topbar-orbit-ia" className="hidden md:flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#eef2ff] text-[#1d4ed8] text-sm font-semibold hover:bg-[#e0e9ff]">
      <OrbitAI className="w-4 h-4"/>Orbit IA
    </button>
  );
}

/* ── Sélecteur d'espace (Super Admin plateforme uniquement) ─────────── */
export function TenantSwitcher() {
  const { user, refreshUser } = useAuth();
  const [tenants, setTenants] = useState([]);
  useEffect(() => { if (user?.is_super_admin) api.get("/tenants").then(r => setTenants(r.data)).catch(() => {}); }, [user?.is_super_admin]);
  if (!user?.is_super_admin) return null;
  const sw = async (slug) => {
    try {
      const { data } = await api.post("/auth/switch-tenant", { slug });
      if (data.access_token) localStorage.setItem("orbit_token", data.access_token);
      localStorage.setItem("orbit_tenant", slug);
      toast.success(`Espace ${data.user?.tenant?.name || slug}`);
      window.location.assign("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <select data-testid="topbar-tenant-switcher" value={user?.tenant_id || ""} onChange={e => { const t = tenants.find(x => x.id === e.target.value); if (t) sw(t.slug); }}
            className="hidden md:block h-10 px-3 rounded-lg border border-violet-200 bg-violet-50 text-violet-800 text-xs font-bold outline-none" title="Super Admin : changer d'espace">
      {tenants.map(t => <option key={t.id} value={t.id}>{t.name} · {t.slug}</option>)}
    </select>
  );
}
